import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';

import { parser } from '@klaviyo-mkt/blocks/parser';

import BlogCards from '@klaviyo-mkt/gatsby-components/organisms/blog-cards';
import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';
import ArticleTableOfContents from '@klaviyo-mkt/gatsby-components/organisms/article-table-of-contents';
import AuthorsParentComponent from '@klaviyo-mkt/gatsby-components/organisms/authors';
import BlogArticleHero from '@klaviyo-mkt/gatsby-components/organisms/blog-article-hero';
import { Breadcrumbs } from '@klaviyo-mkt/gatsby-components/molecules/breadcrumbs';
import { ModulesGrid } from '@klaviyo-mkt/gatsby-components/utils/grid';
import KlaviyoForm from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-form';
import SideDownloadCta from '@klaviyo-mkt/gatsby-components/molecules/side-download-cta';
import { hasSideDownloadCtaData } from '@klaviyo-mkt/gatsby-components/molecules/side-download-cta/utils';
import { generateClassName, windowIsDefined } from '@klaviyo-mkt/gatsby-components/shared/utils/functions';
import { useBreadcrumbs, useShouldDisplayBreadcrumbs } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBreadcrumbs';

import { BasicArticleHero } from './children/basic-article-hero';

import useAddEvent from './hooks/useAddEvent';

import '@klaviyo-mkt/blocks/blocks.scss';
import * as styles from './styles/editorial.module.scss';

import type { IBlogCardsPost } from '@klaviyo-mkt/gatsby-components/organisms/blog-cards/types';
import type { TPostLayout } from './types';

const PostLayout = ({ data }: { data: TPostLayout; }) => {
  const { i18n, t } = useTranslation();
  const {
    authors,
    categories,
    content,
    customers,
    date,
    featuredImage,
    format = 'rich',
    industries,
    isPost,
    platforms,
    templatesEditorial: {
      footerForm,
      metaHeaderCopy: {
        displayAuthorsInFooter = false,
        displayAuthorsDate = false,
        displayBreadcrumbs = true,
        displayCategories = false,
      } = {},
      postTitle: {
        shortTitle = '',
      } = {},
      sideDownloadCta: {
        backgroundColor = 'cotton',
        bodyCopy = '',
        buttons = [],
        headline = '',
        thumbnail,
      } = {},
      showRelatedContent = true,
      showRelatedContentGrayLine = true,
      showTableOfContents = true,
    } = {},
    timeToRead,
    title,
    uri,
  } = data;

  const locationObject = useLocation();
  const windowLocation = `${locationObject.origin}${locationObject.pathname}${locationObject.search}`;
  const encodedUrl = encodeURIComponent(windowLocation);

  const [showCopied, setShowCopied] = useState(false);
  const articleContentRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('');
  const [pageHeight, setPageHeight] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [blogBreadcrumb, setBlogBreadcrumb] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const handleScroll = () => {
    // Get the container that has the blog content
    const blockContent = document.querySelector('.blockContent');
    // Get the height of the blockContent container
    const blockContentHeight = blockContent?.scrollHeight || 0;
    // Get the first text element (either the heading or paragraph) and get the distance from the top of the screen for that first text element
    const firstTextElementPostion = blockContent?.querySelector('h2, p')?.getBoundingClientRect()?.top || 0;
    // Take a thirty percent of the content height as the offset amount meaning when this amount of the content is off screen we show the button
    const offsetHeight = blockContentHeight * 0.3;
    // Show the button if the container is more than twice the viewport height (dont show the button if there is not much to scroll)
    // AND the top of the first text element is more than the offset above the top of the visible screen
    if ((blockContentHeight > 2 * window.innerHeight) && (firstTextElementPostion <=  -(offsetHeight || 0))) {
      setShowButton(true);
    } else {
      setShowButton(false);
    }
  };

  useEffect(() => {
    if (windowIsDefined()) {
      setIsMobile(window.innerWidth <= 768);
    }

    window.addEventListener('scroll', handleScroll);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleWindowResize = () => {
    if (windowIsDefined()) {
      setPageHeight(window.innerHeight);
      setIsMobile(window.innerWidth <= 768);
    }
  };

  useAddEvent();

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    if (windowIsDefined() && window.location.pathname.includes('/blog/')) {
      setBlogBreadcrumb(styles.blogBreadCrumbs);
    } else {
      setBlogBreadcrumb('');
    }
  }, []);
  
  useEffect(() => {
    const handleIntersection: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
  
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: `-90px 0px ${(pageHeight / 2) * -1}px 0px`,
      threshold: 0,
    });

    if (articleContentRef.current) {
      Array.from(articleContentRef.current.getElementsByTagName('h2'))
        .forEach((h2) => {
          observer.observe(h2);
        });
    }

    return () => observer.disconnect();
  }, [articleContentRef, pageHeight]);

  const firstCategory = categories?.nodes?.[0];
  const showCategory = (categories?.nodes.length > 0 && categories?.nodes[0]?.name !== 'Uncategorized' && displayCategories);
  const firstThreeCategoryPosts = (firstCategory?.posts?.nodes || []).filter((node: IBlogCardsPost) => node.title !== title).slice(0, 3);
  const authorProp = authors?.nodes?.map((author) => {
    return {
      image: author?.usersAuthor?.profilePhoto,
      link: author?.uri,
      name: author.name,
    };
  });

  const breadcrumbs = useBreadcrumbs(uri, shortTitle, i18n.language);
  const { shouldShowBreadcrumbs } = useShouldDisplayBreadcrumbs(uri, 'default', displayBreadcrumbs, i18n.language);

  const { h2List, parsedContent } = parser(content || '');
  const hasSideDownloadCta = hasSideDownloadCtaData({ backgroundColor, bodyCopy, buttons, headline, thumbnail });

  const hasTocData = (h2List?.length > 0);
  const handleCopy = () => {
    navigator.clipboard.writeText(windowLocation);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1500); // Show "Copied!" for 1.5 seconds
  };
  return (
    <>
      {shouldShowBreadcrumbs && (
        <Breadcrumbs breadcrumbs={breadcrumbs} className={generateClassName([styles.postBreadcrumbs, blogBreadcrumb])} />
      )}

      {(format === 'rich') && (
        <BlogArticleHero
          authors={authorProp}
          category={showCategory ? {
            link: firstCategory.link,
            name: firstCategory.name,
          } : undefined}
          date={date}
          displayAuthorDate={displayAuthorsDate}
          headline={title}
          readTime={timeToRead}
        />
      )}

      <ModulesGrid className={styles.styledPost}>

        {(format === 'plain') && (
          <BasicArticleHero
            customers={customers}
            featuredImage={featuredImage}
            industries={industries}
            platforms={platforms}
            title={title}
          />
        )}

        {(format === 'rich') && (hasTocData || hasSideDownloadCta) && (
          <div className={styles.sidebar}>
            <div className={styles.sidebarStickyChildren}>
              {hasTocData && showTableOfContents && (
                <ArticleTableOfContents
                  className={styles.tableOfContents}
                  currentSection={activeSection}
                  headingTag='h2'
                  sections={h2List}
                />
              )}
              {hasSideDownloadCta && (
                <SideDownloadCta
                  backgroundColor={backgroundColor}
                  bodyCopy={bodyCopy}
                  buttons={buttons}
                  headline={headline}
                  thumbnail={thumbnail}
                />
              )}
              <div className={styles.socialIcons}>
                <button onClick={handleCopy} className={styles.shareLink} rel="noopener">
                  <Icon iconName="Copy" className={styles.icon} fillColor="currentColor"/>
                </button>
                {showCopied && <div className={styles.copiedText}>Copied!</div>}
                <a className={styles.shareLink} rel="noopener" href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}>
                  <Icon className={styles.icon} iconName="Facebook"  fillColor="currentColor"/>
                </a>
                <a className={styles.shareLink} rel="noopener"  href={`https://twitter.com/intent/tweet?url=${encodedUrl}`}>
                  <Icon className={styles.icon} iconName="Twitter"  fillColor="currentColor"/>
                </a>
                <a className={styles.shareLink} rel="noopener" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}>
                  <Icon className={styles.icon} iconName="LinkedIn"  fillColor="currentColor"/>
                </a>
              </div>
            </div>
          </div>
        )}

        {content && parsedContent && (
          <div className='blockContent' ref={articleContentRef}>
            <hr className='wp-block-separator' />
            {parsedContent}
          </div>
        )}
      </ModulesGrid>
      {authors && isPost && displayAuthorsInFooter && (
        <AuthorsParentComponent noTableOfContents={!hasTocData && !hasSideDownloadCta} authors={authors.nodes} />
      )}
      {showRelatedContent && firstThreeCategoryPosts.length > 0 &&
        <div className={generateClassName(['grid', styles.relatedContentGrid])}>
          <div className={styles.relatedContent}>
            <BlogCards
              backgroundColor="white"
              displayGrayLine={showRelatedContentGrayLine}
              canShowViewAll={false}
              headline={t('molecules_blogCard_related_content')}
              cardTitleSize={isMobile ? 'h5' : 'h6'}
              category={{
                name: firstCategory.name,
                link: firstCategory.link,
                posts: firstThreeCategoryPosts,
              }}
            />
          </div>
        </div>
      }
      {footerForm?.useForm && (
        <KlaviyoForm
          backgroundColor={footerForm.form.klaviyoForm?.backgroundColor || 'light'}
          formId={footerForm.form.klaviyoForm?.formId}
        />
      )}
      {showButton && (
          <div className={styles.backToTopButtonContainer}>
            <button
              onClick={() => {
                const blockContent = document.querySelector('.blockContent');
                const fixedHeaderHeight = 120;
                if (blockContent) {
                  const blockContentTop = blockContent.getBoundingClientRect().top;
                  const scrollValue = window.scrollY + blockContentTop - fixedHeaderHeight;
                  window.scrollTo({
                    top: scrollValue,
                    behavior: 'smooth',
                  });
                }
              }}
              className={styles.backToTopButton}
            >
              <Icon iconName='ArrowDown' size={1.8} fillColor='charcoal' />
            </button>
          </div>
      )}
    </>
  );
};

export default PostLayout;
